import { Checkbox, DatePicker } from 'antd';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getCacNotViewedStudents,
  getCacUsageReport,
} from '../../api/statistics.api';
import Button from '../../components/Button';
import DownloadButton from '../../components/DownloadButton';
import ErrorResponse from '../../components/ErrorResponse';
import Loader from '../../components/Loader';
import SimpleHeader from '../../components/layout/SimpleHeader';
import SelectCampus from '../../components/SelectCampus';
import SelectCampusMultiChoice from '../../components/SelectCampusMultiChoice';
import TableEmpty from '../../components/common/TableEmpty';
import CacReportTabs, { ICacTabItem } from '../../components/statistics/cac/CacReportTabs';
import CacReportTable, {
  ICacReportTableRow,
} from '../../components/statistics/cac/CacReportTable';
import CacRatioMatrix from '../../components/statistics/cac/CacRatioMatrix';
import CacNotViewedTable from '../../components/statistics/cac/CacNotViewedTable';
import TabItem from '../../components/statistics/TabItem';
import useAxios from '../../hooks/useAxios';
import useNotification from '../../hooks/useNotification';
import {
  campusHeadOffice,
  UNLIMITED_PAGE_SIZE,
} from '../../lib/data';
import {
  CAMPUS_DIV_DIRECT,
  CAMPUS_DIV_FRANCHISE,
} from '../../constans';
import exportCacReportWorkbook from '../../lib/exportCacReport';
import {
  buildCacMatrixForExportTab,
  buildCacReportRowsForTab,
  CAC_TAB_DIRECT_SUM,
  CAC_TAB_FRANCHISE_SUM,
  CAC_TAB_OVERALL,
  rowHeaderLabelForCacTab,
} from '../../lib/cacUsageTabRows';
import exportCacNotViewed from '../../lib/exportCacNotViewed';
import { ICampusItem } from '../../models/campuses.model';
import {
  IGetCacNotViewedStudents,
  IGetCacUsageReport,
} from '../../models/statistics.model';
import { RootState } from '../../store/rootReducer';

const NOT_VIEWED_PAGE_SIZE = 50;

/** 상단 큰 탭: 활용 통계 / 미조회 학생 */
enum MainTab {
  Usage = 0,
  NotViewed = 1,
}

const StatisticsCac = () => {
  const { t } = useTranslation();
  const { infoNotification } = useNotification();
  const { auth, currentCampus } = useSelector(
    (state: RootState) => state.auth,
  );
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const {
    data: usageRes,
    isLoading: usageLoading,
    error: usageError,
    execute: executeUsage,
  } = useAxios<IGetCacUsageReport>();
  const {
    data: notViewedRes,
    isLoading: notViewedLoading,
    error: notViewedError,
    execute: executeNotViewed,
  } = useAxios<IGetCacNotViewedStudents>();

  const [mainTab, setMainTab] = useState<number>(MainTab.Usage);
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  /** 학생 스냅샷 기준일(YYYYMMDD) — 기간과 함께 필수 */
  const [baseDate, setBaseDate] = useState('');
  const [srtDate, setSrtDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [excludeUpperLevels, setExcludeUpperLevels] = useState(false);
  const [excludeMiddleLevels, setExcludeMiddleLevels] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState<string>(CAC_TAB_OVERALL);
  const [hasSearched, setHasSearched] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  /** 검색 적용 시점의 필터 — 엑셀 다운로드와 페이지 변경 시 재호출에 사용 */
  const [appliedFilter, setAppliedFilter] = useState<{
    campusCodes: string[];
    srtDate: string;
    endDate: string;
    baseDate: string;
    excludeUpperLevels: boolean;
    excludeMiddleLevels: boolean;
  } | null>(null);

  /** 활용 통계 응답 페이로드 */
  const usagePayload = usageRes?.data;
  /** 미조회 학생 응답 데이터 */
  const notViewedRows = notViewedRes?.data ?? [];
  const notViewedTotal = notViewedRes?.total ?? 0;

  const operatingCampuses = useMemo(() => {
    if (!campusData?.length) return [];
    return campusData.filter(
      (c) =>
        c.campusStatus === 'NM' &&
        c.testYn === 'N' &&
        c.campusCode !== campusHeadOffice,
    );
  }, [campusData]);

  const accessibleCampuses = useMemo(() => {
    if (!operatingCampuses.length) return [];
    if (auth.hqYn === 'Y') return operatingCampuses;
    return operatingCampuses.filter((c) => auth.campus.includes(c.campusCode));
  }, [auth.campus, auth.hqYn, operatingCampuses]);

  const hqTabs: ICacTabItem[] = useMemo(() => {
    if (auth.hqYn !== 'Y') return [];

    const direct = accessibleCampuses.filter(
      (c) => c.campusDiv === CAMPUS_DIV_DIRECT,
    );
    const franchise = accessibleCampuses.filter(
      (c) => c.campusDiv === CAMPUS_DIV_FRANCHISE,
    );
    const sortByCampusCode = (a: ICampusItem, b: ICampusItem) =>
      a.campusCode.localeCompare(b.campusCode, undefined, { numeric: true });

    const tabs: ICacTabItem[] = [
      { key: CAC_TAB_OVERALL, label: '종합' },
      { key: CAC_TAB_DIRECT_SUM, label: '직영 소계' },
      ...[...direct].sort(sortByCampusCode).map((c) => ({
        key: `campus:${c.campusCode}`,
        label: c.campusKorName,
      })),
      { key: CAC_TAB_FRANCHISE_SUM, label: '가맹 소계' },
      ...[...franchise].sort(sortByCampusCode).map((c) => ({
        key: `campus:${c.campusCode}`,
        label: c.campusKorName,
      })),
    ];
    return tabs;
  }, [accessibleCampuses, auth.hqYn]);

  const branchTabs: ICacTabItem[] = useMemo(() => {
    if (auth.hqYn === 'Y') return [];
    const list = currentCampus
      ? accessibleCampuses.filter((c) => c.campusCode === currentCampus)
      : accessibleCampuses;
    return [...list]
      .sort((a, b) =>
        a.campusCode.localeCompare(b.campusCode, undefined, { numeric: true }),
      )
      .map((c) => ({
        key: `campus:${c.campusCode}`,
        label: c.campusKorName,
      }));
  }, [accessibleCampuses, auth.hqYn, currentCampus]);

  const visibleTabs = auth.hqYn === 'Y' ? hqTabs : branchTabs;

  useEffect(() => {
    if (!visibleTabs.length) return;
    const exists = visibleTabs.some((tab) => tab.key === activeTabKey);
    if (!exists) {
      setActiveTabKey(visibleTabs[0].key);
    }
  }, [visibleTabs, activeTabKey]);

  const rowHeaderLabel = rowHeaderLabelForCacTab(activeTabKey);

  /** 캠퍼스 권한이고 캠퍼스 탭일 때만 매트릭스 노출 */
  const showRatioMatrix =
    auth.hqYn === 'N' && activeTabKey.startsWith('campus:');

  const matrixExport = useMemo(
    () =>
      buildCacMatrixForExportTab(
        activeTabKey,
        usagePayload,
        campusData,
        auth.hqYn,
      ),
    [activeTabKey, usagePayload, campusData, auth.hqYn],
  );

  /** 활성 탭에 맞는 표 행 데이터 매핑 */
  const reportRows: ICacReportTableRow[] = useMemo(
    () =>
      buildCacReportRowsForTab(
        activeTabKey,
        usagePayload,
        accessibleCampuses,
      ) as ICacReportTableRow[],
    [usagePayload, activeTabKey, accessibleCampuses],
  );

  type AppliedFilter = NonNullable<typeof appliedFilter>;

  /** 미조회 학생 API용 캠퍼스 — 본사: 검색 시 저장된 filter.campusCodes(멀티), 캠퍼스 직원: currentCampus */
  const resolveNotViewedCampusCodes = (filter: AppliedFilter): string[] => {
    if (auth.hqYn === 'Y') {
      return filter.campusCodes;
    }
    return currentCampus ? [currentCampus] : [];
  };

  const fetchUsage = (filter: AppliedFilter) => {
    executeUsage(
      getCacUsageReport({
        campusCodes: filter.campusCodes,
        srtDate: filter.srtDate,
        endDate: filter.endDate,
        baseDate: filter.baseDate,
        excludeUpperLevels: filter.excludeUpperLevels ? 'Y' : 'N',
        excludeMiddleLevels: filter.excludeMiddleLevels ? 'Y' : 'N',
      }),
    );
  };

  const fetchNotViewed = (targetPageNo: number, filter: AppliedFilter) => {
    const codes = resolveNotViewedCampusCodes(filter);
    if (auth.hqYn !== 'Y' && codes.length === 0) return;
    executeNotViewed(
      getCacNotViewedStudents({
        campusCodes: codes,
        srtDate: filter.srtDate,
        endDate: filter.endDate,
        baseDate: filter.baseDate,
        excludeUpperLevels: filter.excludeUpperLevels ? 'Y' : 'N',
        excludeMiddleLevels: filter.excludeMiddleLevels ? 'Y' : 'N',
        pageNo: targetPageNo,
        perPage: NOT_VIEWED_PAGE_SIZE,
      }),
    );
  };

  /** 캠퍼스 직원: 네비 currentCampus 변경 시 campusCodes 반영 후 재조회 */
  useEffect(() => {
    if (!appliedFilter || !currentCampus) return;
    if (auth.hqYn === 'Y') return;
    setPageNo(1);
    const next = { ...appliedFilter, campusCodes: [currentCampus] };
    setAppliedFilter(next);
    if (mainTab === MainTab.Usage) {
      fetchUsage(next);
    } else {
      fetchNotViewed(1, next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCampus]);

  const onSearch = () => {
    if (!baseDate || !srtDate || !endDate) {
      return infoNotification(t('student.student.message16'));
    }
    const days = moment(endDate).diff(moment(srtDate), 'days') + 1;
    if (days <= 0) {
      return infoNotification(t('statistics.web.message1'));
    }

    let targetCampusCodes: string[] = [];

    if(auth.hqYn === 'Y') {
      if(mainTab !== MainTab.Usage) {
        targetCampusCodes = campusCodes;
      }
    } else {
      targetCampusCodes = currentCampus ? [currentCampus] : [];
    }

    const filter: AppliedFilter = {
      campusCodes: targetCampusCodes,
      srtDate,
      endDate,
      baseDate,
      excludeUpperLevels,
      excludeMiddleLevels,
    };

    setHasSearched(true);
    setAppliedFilter(filter);
    setPageNo(1);

    if (mainTab === MainTab.Usage) {
      fetchUsage(filter);
    } else {
      fetchNotViewed(1, filter);
    }
  };

  const onChangePage = (newPage: number) => {
    if (!appliedFilter || newPage === pageNo) return;
    setPageNo(newPage);
    fetchNotViewed(newPage, appliedFilter);
  };

  const onChangeMainTab = (newTab: number) => {
    if (newTab === mainTab) return;
    setMainTab(newTab);
    setPageNo(1);
    if (!appliedFilter) return;

    if (newTab === MainTab.Usage) {
      if(auth.hqYn === 'Y') {
        appliedFilter.campusCodes = [];
      }
      if(!usagePayload) fetchUsage(appliedFilter);
    } else {
      if (!notViewedRes) fetchNotViewed(1, appliedFilter);
    }
  };

  const onDownload = async () => {
    if (!appliedFilter) {
      return infoNotification(t('statistics.appday.text1'));
    }

    if (mainTab === MainTab.Usage) {
      if (!usagePayload) {
        return infoNotification(t('statistics.appday.text1'));
      }
      const tabs = visibleTabs.map((tab) => ({
        sheetName: tab.label,
        rowHeaderLabel: rowHeaderLabelForCacTab(tab.key),
        reportRows: buildCacReportRowsForTab(
          tab.key,
          usagePayload,
          accessibleCampuses,
        ),
        matrix: buildCacMatrixForExportTab(
          tab.key,
          usagePayload,
          campusData,
          auth.hqYn,
        ),
      }));
      exportCacReportWorkbook(
        {
          srtDate: appliedFilter.srtDate,
          endDate: appliedFilter.endDate,
          baseDate: appliedFilter.baseDate,
          excludeUpperLevels: appliedFilter.excludeUpperLevels,
          excludeMiddleLevels: appliedFilter.excludeMiddleLevels,
        },
        tabs,
      );
      return;
    }

    const codes = resolveNotViewedCampusCodes(appliedFilter);
    if (auth.hqYn !== 'Y' && codes.length === 0) {
      return infoNotification(t('statistics.appday.text1'));
    }
    try {
      const { data } = await getCacNotViewedStudents({
        campusCodes: codes,
        srtDate: appliedFilter.srtDate,
        endDate: appliedFilter.endDate,
        baseDate: appliedFilter.baseDate,
        excludeUpperLevels: appliedFilter.excludeUpperLevels ? 'Y' : 'N',
        excludeMiddleLevels: appliedFilter.excludeMiddleLevels ? 'Y' : 'N',
        pageNo: 1,
        perPage: UNLIMITED_PAGE_SIZE,
      });
      exportCacNotViewed({
        srtDate: appliedFilter.srtDate,
        endDate: appliedFilter.endDate,
        baseDate: appliedFilter.baseDate,
        excludeUpperLevels: appliedFilter.excludeUpperLevels,
        excludeMiddleLevels: appliedFilter.excludeMiddleLevels,
        students: data?.data ?? [],
      });
    } catch {
      infoNotification(t('statistics.appday.text1'));
    }
  };

  const canDownload =
    hasSearched &&
    !!appliedFilter &&
    ((mainTab === MainTab.Usage &&
      !usageLoading &&
      !!usagePayload &&
      reportRows.length > 0) ||
      (mainTab === MainTab.NotViewed &&
        !notViewedLoading &&
        notViewedRows.length > 0));

  const renderUsageContent = () => (
    <>
      {visibleTabs.length > 0 && (
        <CacReportTabs
          tabs={visibleTabs}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
        />
      )}
      {showRatioMatrix && matrixExport && (
        <CacRatioMatrix
          divisionLabel={matrixExport.divisionLabel}
          campusLabel={matrixExport.campusLabel}
          canbAll={matrixExport.canbAll}
          divisionSum={matrixExport.divisionSum}
          campus={matrixExport.campus}
        />
      )}
      {reportRows.length === 0 && !usageLoading ? (
        <TableEmpty text={t('statistics.appday.text1')} />
      ) : (
        /* antd Table loading 끔: content 영역 Loader와 이중 표시 방지 */
        <CacReportTable
          rowHeaderLabel={rowHeaderLabel}
          rows={reportRows}
          loading={false}
        />
      )}
      {canDownload && (
        <div className="comm-bx__space">
          <DownloadButton onClick={onDownload} />
        </div>
      )}
    </>
  );

  const renderNotViewedContent = () => (
    <>
      {auth.hqYn === 'Y' && (
        <div className="comm-bx__space mb-3 d-flex align-items-center">
          <label className="mr-2">{t('common.menu.campus')}</label>
          <SelectCampusMultiChoice
            placeholder={t('statistics.eLearning.title2')}
            onChange={setCampusCodes}
            value={campusCodes}
          />
          <label className='ml-2'>
            (
            {campusCodes.length
              ? `${campusCodes.length}${t('statistics.attendance.text1')}`
              : t('common.text.all')}
            )
          </label>
        </div>
      )}
      {notViewedRows.length === 0 && !notViewedLoading ? (
        <TableEmpty text={t('statistics.appday.text1')} />
      ) : (
        /* Loader만 사용 */
        <CacNotViewedTable
          rows={notViewedRows}
          total={notViewedTotal}
          pageNo={pageNo}
          setPageNo={onChangePage}
          pageSize={NOT_VIEWED_PAGE_SIZE}
          loading={false}
        />
      )}
      {canDownload && (
        <div className="comm-bx__space">
          <DownloadButton onClick={onDownload} />
        </div>
      )}
    </>
  );

  return (
    <>
      <SimpleHeader
        iconFile="icon-statistics-big.svg"
        parentMenu={t('common.menu.statistics')}
        currentMenu="CAC 활용 통계"
        onlyTitle={true}
      />

      <div className="common-search">
        <div className="search-area">
          <ul className="search-bx">
            <li>
              <label>학생 기준일</label>
              <div className="date-picker__area">
                <DatePicker
                  value={baseDate ? moment(baseDate) : undefined}
                  onChange={(e) => {
                    if (e === null) setBaseDate('');
                    else setBaseDate(moment(e).format('YYYYMMDD'));
                  }}
                  format={['YYYY-MM-DD', 'YYYYMMDD']}
                />
              </div>
            </li>
            <li>
              <label>{t('sms.list.column1')}</label>
              <div className="date-picker__area">
                <DatePicker
                  value={srtDate ? moment(srtDate) : undefined}
                  onChange={(e) => {
                    if (e === null) setSrtDate('');
                    else setSrtDate(moment(e).format('YYYYMMDD'));
                  }}
                  format={['YYYY-MM-DD', 'YYYYMMDD']}
                />
              </div>
              ~
              <div className="date-picker__area">
                <DatePicker
                  value={endDate ? moment(endDate) : undefined}
                  onChange={(e) => {
                    if (e === null) setEndDate('');
                    else setEndDate(moment(e).format('YYYYMMDD'));
                  }}
                  format={['YYYY-MM-DD', 'YYYYMMDD']}
                />
              </div>
              <div className="comm-btn__area">
                <Button
                  className="btn-basic small black"
                  onClick={onSearch}
                  text={t('common.button.search')}
                />
              </div>
            </li>
            <li>
              <label>&nbsp;</label>
              <Checkbox
                checked={excludeUpperLevels}
                onChange={(e) => setExcludeUpperLevels(e.target.checked)}
              >
                Deca·Hendeca 제외
              </Checkbox>
              <Checkbox
                style={{ marginLeft: 16 }}
                checked={excludeMiddleLevels}
                onChange={(e) => setExcludeMiddleLevels(e.target.checked)}
              >
                중등 제외
              </Checkbox>
            </li>
          </ul>
        </div>
      </div>

      <div className="content scrollbar">
        {(usageLoading || notViewedLoading) && <Loader />}
        <ErrorResponse
          error={mainTab === MainTab.Usage ? usageError : notViewedError}
        />

        <div className="comm-tab__line">
          <ul>
            <TabItem
              currentContent={mainTab}
              onSetContent={onChangeMainTab}
              tabContent={MainTab.Usage}
              tabLabel="활용 통계"
            />
            <TabItem
              currentContent={mainTab}
              onSetContent={onChangeMainTab}
              tabContent={MainTab.NotViewed}
              tabLabel="미조회 학생"
            />
          </ul>
        </div>

        {!hasSearched ? (
          <TableEmpty text={t('statistics.appday.text1')} />
        ) : mainTab === MainTab.Usage ? (
          renderUsageContent()
        ) : (
          renderNotViewedContent()
        )}
      </div>
    </>
  );
};

export default StatisticsCac;
